import ReactEcharts from "echarts-for-react";
import _ from "lodash";
import { hexColor } from "../../../../../enums/colors";
import { TbCircleFilled } from "react-icons/tb";
import { t } from "i18next";
import { DataRow } from "../../../../../stores/TabsStore";
import { useAppStore } from "../../../../../stores/RootStore";
import { Person } from "../../../../../types/types";
import { Duration } from "luxon";


export const PersonsLoaded = ({data}: {data: DataRow[]}) => {
  const {theme} = useAppStore()
  let colorPalette = [hexColor.worked, hexColor.stopped, hexColor.turned_off]
  
  const getOptions = () => ({
    series: [
      {
        name: `summary`,
        type: "pie",
        radius: ['55%', '90%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 6,
          borderColor: theme === 'light' ? hexColor.background : hexColor.foreground,
          borderWidth: 2
        },
        label: {
          show: false,
          position: 'center'
        },
        labelLine: {
          show: false
        },
        color: colorPalette,
        data: [
          { name: "work", value: _.sumBy(data, "works.worked.seconds"), color: hexColor.worked },
          { name: "idle", value: _.sumBy(data, "works.stopped.seconds"), color: hexColor.stopped },
          { name: "maintenanceWorked", value: _.sumBy(data, "maintenance.worked.seconds"), color: hexColor.maintenanceWorked},
          { name: "maintenanceStopped", value: _.sumBy(data, "maintenance.stopped.seconds"), color: hexColor.maintenanceStopped},
          { name: "repair", value: _.sumBy(data, "repair.total.seconds"), color: hexColor.repair},
          { name: "service", value: _.sumBy(data, "service.total.seconds"), color: hexColor.service},
        ],
      },
    ]
  });


  return (
    <div className="flex flex-col w-full text-sm my-4 px-2">
      <div className="grid grid-cols-1 lg:grid-cols-3 my-4 gap-4">
        <div className="flex w-full justify-center lg:px-6">
          <ReactEcharts option={getOptions()} style={{ height: 120, width: 120 }} opts={{renderer: 'svg'}} />
        </div>
        <div className="flex flex-col w-full gap-3 lg:border-l border-border lg:px-6">
            <div className="grid grid-cols-2">
              <div className="flex font-semibold">{t('total_interaction')}</div>
              <div className="flex font-semibold">{Duration.fromObject({seconds: _.sumBy(data, "availability.on.seconds") + _.sumBy(data, "unavailability.on.seconds") }).toFormat('hh:mm:ss')}</div>
            </div>
            <div className="grid grid-cols-2">
              <div className="flex flex-col">
                <div className="flex items-center space-x-2">
                  <TbCircleFilled className="w-3 h-3" style={{color: hexColor.worked}}/>
                  <span className="text-secondary-foreground">{t('interaction_worked')}:</span>
                </div>
                <span className="font-medium text-base">{Duration.fromObject({seconds: _.sumBy(data, "works.worked.seconds") + _.sumBy(data, "maintenance.worked.seconds")}).toFormat('hh:mm:ss')} ({(((_.sumBy(data, "works.worked.seconds") + _.sumBy(data, "maintenance.worked.seconds")) / (_.sumBy(data, "works.on.seconds") + _.sumBy(data, "maintenance.on.seconds"))) * 100).toFixed(2)} %)</span>
              </div>
              <div className="flex flex-col">
                <div className="flex items-center space-x-2">
                  <TbCircleFilled className="w-3 h-3" style={{color: hexColor.stopped}}/>
                  <span className="text-secondary-foreground">{t('interaction_stopped')}:</span>
                </div>
                <span className="font-medium text-base">{Duration.fromObject({seconds: _.sumBy(data, "works.stopped.seconds") + _.sumBy(data, "maintenance.stopped.seconds")}).toFormat('hh:mm:ss')} ({(((_.sumBy(data, "works.stopped.seconds") + _.sumBy(data, "maintenance.stopped.seconds")) / (_.sumBy(data, "works.on.seconds") + _.sumBy(data, "maintenance.on.seconds"))) * 100).toFixed(2)} %)</span>
              </div>
            </div>
            <div className="grid grid-cols-2">
              <div className="flex flex-col">
                <div className="flex items-center space-x-2">
                  <span className="text-secondary-foreground">{t('interaction_as_manual')}:</span>
                </div>
                <span className="font-medium text-base">{Duration.fromObject({seconds: _.sumBy(data, "works.on.seconds")}).toFormat('hh:mm:ss')}</span>
              </div>
              <div className="flex flex-col">
                <div className="flex items-center space-x-2">
                  <span className="text-secondary-foreground">{t('interaction_as_maintenance')}:</span>
                </div>
                <span className="font-medium text-base">{Duration.fromObject({seconds: _.sumBy(data, "maintenance.on.seconds")}).toFormat('hh:mm:ss')}</span>
              </div>
            </div>
        </div>
        <div className="flex flex-col w-full gap-3 lg:border-l border-border lg:px-6">
          <div className="grid grid-cols-2">
            <div className="flex font-semibold">{t('interaction_equipment_count')}</div>
            <div className="flex font-semibold">{_.uniq(data.map(r => r.equipment.id)).length}</div>
          </div>
          <div className="grid grid-cols-2">
            <div className="flex flex-col">
              <div className="flex items-center space-x-2">
                <TbCircleFilled className="w-3 h-3" style={{color: hexColor.turned_off}}/>
                <span className="text-secondary-foreground">{t('interaction_as_repair')}:</span>
              </div>
              <span className="font-medium text-base">{Duration.fromObject({seconds: _.sumBy(data, "repair.total.seconds")}).toFormat('hh:mm:ss')} ({((_.sumBy(data, "repair.total.percent") / data.length) * 100).toFixed(2)} %)</span>
            </div>
            <div className="flex flex-col">
              <div className="flex items-center space-x-2">
                <TbCircleFilled className="w-3 h-3" style={{color: hexColor.turned_off}}/>
                <span className="text-secondary-foreground">{t('interaction_as_service')}:</span>
              </div>
              <span className="font-medium text-base">{Duration.fromObject({seconds: _.sumBy(data, "service.total.seconds")}).toFormat('hh:mm:ss')} ({((_.sumBy(data, "service.total.percent") / data.length) * 100).toFixed(2)} %)</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}